// Google OAuth Service
// Handles Google OAuth flow and backend integration
// =====================================================

import { toCustomerError } from '../utils/customerError';

import { API_BASE_URL } from '../config/apiEndpoints';
const GOOGLE_CLIENT_ID = '72503172301-9pv7vbddhboa247eep3v4856apam2sf8.apps.googleusercontent.com';

class GoogleAuthService {
  constructor() {
    this.isGoogleLoaded = false;
    this.googleClient = null;
  }

  // Initialize Google OAuth
  async initializeGoogleAuth() {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) {
        this.isGoogleLoaded = true;
        this.initializeGoogleClient();
        resolve(true);
      } else {
        // Load Google OAuth script
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          this.isGoogleLoaded = true;
          this.initializeGoogleClient();
          resolve(true);
        };
        script.onerror = () => {
          reject(new Error('Failed to load Google OAuth script'));
        };
        document.head.appendChild(script);
      }
    });
  }

  // Initialize Google OAuth client
  initializeGoogleClient() {
    if (!this.isGoogleLoaded) return;

    try {
      this.googleClient = window.google.accounts.oauth2.initCodeClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        ux_mode: 'popup'
      });
    } catch (error) {
    }
  }

  // Handle Google OAuth token callback (for access token)
  async handleGoogleTokenCallback(googleAccessToken) {
    try {

      // Prepare the request payload
      const requestPayload = {
        google_token: googleAccessToken  // Send Google's access token
      };
      

      // Send Google access token to your backend
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });


      if (!response.ok) {
        // Try to get error details from response body
        let errorMessage = 'Failed to authenticate';
        try {
          const errorBody = await response.text();
          
          // Try to parse as JSON to get detailed error message
          try {
            const errorJson = JSON.parse(errorBody);
            // Handle different error response formats
            if (errorJson.detail) {
              errorMessage = errorJson.detail;
            } else if (errorJson.message) {
              errorMessage = errorJson.message;
            } else if (errorJson.error) {
              errorMessage = typeof errorJson.error === 'string' 
                ? errorJson.error 
                : errorJson.error.message || 'Authentication failed';
            } else if (typeof errorJson === 'string') {
              errorMessage = errorJson;
            }
          } catch (parseError) {
            // If not JSON, use the text as is
            errorMessage = errorBody || `HTTP error! status: ${response.status}`;
          }
        } catch (e) {
          errorMessage = `HTTP error! status: ${response.status}`;
        }
        
        throw new Error(toCustomerError(errorMessage, { status: response.status, fallback: 'Authentication failed. Please try again.' }));
      }

      const data = await response.json();

      if (data.success) {

        // Store the JWT token and user data
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        

        // Return the data for the AuthContext
        return {
          success: true,
          user: data.user,
          accessToken: data.access_token
        };
      } else {
        throw new Error(data.message || 'Authentication failed');
      }
    } catch (error) {
      throw error;
    }
  }

  // Handle Google OAuth callback (for authorization code)
  async handleGoogleCallback(googleCode) {
    try {

      // Prepare the request payload
      const requestPayload = {
        google_token: googleCode  // Send Google's authorization code
      };
      
      // Send Google code to your backend
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });


      if (!response.ok) {
        // Try to get error details from response body
        let errorMessage = 'Failed to authenticate';
        try {
          const errorBody = await response.text();
          
          // Try to parse as JSON to get detailed error message
          try {
            const errorJson = JSON.parse(errorBody);
            // Handle different error response formats
            if (errorJson.detail) {
              errorMessage = errorJson.detail;
            } else if (errorJson.message) {
              errorMessage = errorJson.message;
            } else if (errorJson.error) {
              errorMessage = typeof errorJson.error === 'string' 
                ? errorJson.error 
                : errorJson.error.message || 'Authentication failed';
            } else if (typeof errorJson === 'string') {
              errorMessage = errorJson;
            }
          } catch (parseError) {
            // If not JSON, use the text as is
            errorMessage = errorBody || `HTTP error! status: ${response.status}`;
          }
        } catch (e) {
          errorMessage = `HTTP error! status: ${response.status}`;
        }
        
        throw new Error(toCustomerError(errorMessage, { status: response.status, fallback: 'Authentication failed. Please try again.' }));
      }

      const data = await response.json();

      if (data.success) {

        // Store the JWT token and user data
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        

        // Return the data for the AuthContext
        return {
          success: true,
          user: data.user,
          accessToken: data.access_token
        };
      } else {
        throw new Error(data.message || 'Authentication failed');
      }
    } catch (error) {
      throw error;
    }
  }

  // Trigger Google OAuth login
  async login() {
    if (!this.isGoogleLoaded) {
      await this.initializeGoogleAuth();
    }

    return new Promise((resolve, reject) => {
      try {
        let isResolved = false;
        let timeoutId = null;
        let popupOpened = false;
        let focusHandler = null;
        let popupCloseCheckTimeout = null;

        // Clean up function
        const cleanup = () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          if (popupCloseCheckTimeout) {
            clearTimeout(popupCloseCheckTimeout);
            popupCloseCheckTimeout = null;
          }
          if (focusHandler) {
            window.removeEventListener('focus', focusHandler);
            window.removeEventListener('blur', focusHandler);
            focusHandler = null;
          }
        };

        // Set a timeout to detect if user closes the popup without completing
        // OAuth flows can take 10-30 seconds, especially if user needs to select account or grant permissions
        timeoutId = setTimeout(() => {
          if (!isResolved) {
            isResolved = true;
            cleanup();
            reject(new Error('Login timed out. Please try again.'));
          }
        }, 30000); // 30 second timeout - more reasonable for OAuth flows

        // Monitor window focus/blur to detect popup closure
        // This is a secondary mechanism - timeout is primary
        let hadFocus = document.hasFocus();
        
        focusHandler = () => {
          const hasFocus = document.hasFocus();
          
          // Detect when popup opens (window loses focus)
          if (!hasFocus && hadFocus && !popupOpened) {
            popupOpened = true;
          }
          
          // Detect when popup might have closed (window regains focus)
          // Only act if popup was opened and we're still waiting
          if (hasFocus && !hadFocus && popupOpened && !isResolved) {
            // Clear any existing check timeout
            if (popupCloseCheckTimeout) {
              clearTimeout(popupCloseCheckTimeout);
            }
            
            // Wait a bit to see if callback fires (user might have completed login)
            // If callback doesn't fire within 3 seconds, assume popup was closed
            popupCloseCheckTimeout = setTimeout(() => {
              if (!isResolved) {
                isResolved = true;
                cleanup();
                reject(new Error('Login was cancelled. Please try again.'));
              }
            }, 3000); // Give 3 seconds for callback to fire after focus returns
          }
          
          hadFocus = hasFocus;
        };

        // Add focus/blur listeners to detect popup closure
        window.addEventListener('focus', focusHandler);
        window.addEventListener('blur', focusHandler);

        // Try using initTokenClient to get access token directly
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'openid email profile',
          callback: async (response) => {
            if (isResolved) return; // Prevent multiple calls
            
            // Clean up all listeners and timers
            cleanup();
            isResolved = true;

            // Check if there's an error in the response
            if (response.error) {
              let errorMsg = 'Authentication was cancelled or failed';
              if (response.error === 'popup_closed_by_user') {
                errorMsg = 'Login was cancelled. Please try again.';
              } else if (response.error_description) {
                errorMsg = response.error_description;
              } else if (response.error) {
                errorMsg = `Authentication error: ${response.error}`;
              }
              reject(new Error(errorMsg));
              return;
            }

            try {
              const result = await this.handleGoogleTokenCallback(response.access_token);
              resolve(result);
            } catch (error) {
              reject(error);
            }
          }
        });
        

        // Use requestAccessToken() method for direct access token flow
        if (tokenClient && typeof tokenClient.requestAccessToken === 'function') {
          tokenClient.requestAccessToken();
        } else {
          cleanup();
          reject(new Error('Token client does not have requestAccessToken method'));
          return;
        }

      } catch (error) {
        reject(error);
      }
    });
  }

  // Get stored user data
  getStoredUser() {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  }

  // Get stored access token
  getStoredToken() {
    return localStorage.getItem('access_token');
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getStoredToken();
    const user = this.getStoredUser();
    return !!(token && user);
  }

  // Logout
  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    
    // Revoke Google token if possible
    if (window.google && this.isGoogleLoaded) {
      try {
        window.google.accounts.oauth2.revoke();
      } catch (error) {
      }
    }
  }

  // Get backend user ID
  getBackendUserId() {
    const user = this.getStoredUser();
    return user ? user.id : null;
  }

  // Make authenticated API calls
  async authenticatedFetch(url, options = {}) {
    const token = this.getStoredToken();
    
    if (!token) {
      throw new Error('No authentication token available');
    }

    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    });
  }
}

// Create singleton instance
const googleAuthService = new GoogleAuthService();

export default googleAuthService;
